import React, { useMemo, useState } from 'react';
import type { LocalSpatialPlan, LocalPlanPoint } from '../../lib/api/geologist';
import type { GeoUiLang } from './geoWorkspaceI18n';

// P7.3.1/§11 — honest internal-coordinate borehole plan. NO Google basemap.
// Anomalous points (bad OCR/parse) are EXCLUDED from the main geometry and never
// relocated/clamped; they are disclosed via a badge + toggle and kept in the table
// with their original values. Axis labels + legend by участок. NO north/east labels.

const LOC_COLORS = ['#0A1F3C', '#2F7A3D', '#1E40AF', '#B45309', '#7c3aed', '#0e7490'];
const W = 620, H = 420, PAD = 44;
const TABLE_PAGE = 10;

function colorForLocation(locations: string[]): Record<string, string> {
  const m: Record<string, string> = {};
  locations.forEach((l, i) => { m[l] = LOC_COLORS[i % LOC_COLORS.length]; });
  return m;
}

const LOCAL_COPY = {
  ru: { inReport: 'в отчёте', site: 'Участок', context: 'Контекст', original: 'Координаты из отчёта', parsed: 'Разобрано системой', source: 'Источник', page: 'стр.', table: 'табл.', review: 'Проверка', required: 'требует проверки', reviewCount: 'требуют проверки', anomalyHidden: 'координатных аномалий не показаны на основном плане', hide: 'Скрыть', showAnomalies: 'Показать аномальные записи', search: 'Поиск по коду скважины', allSites: 'Все участки', xAxis: 'X (локальная, ед.)', yAxis: 'Y (локальная, ед.)', selectPoint: 'Выберите точку или строку, чтобы увидеть детали скважины.', anomalyTitle: 'Аномальные координаты (исходные значения сохранены, не показаны на плане)', code: 'Код', reportCode: 'В отчёте', xLocal: 'X (лок.)', yLocal: 'Y (лок.)', showAll: 'Показать все' },
  hy: { inReport: 'հաշվետվությունում', site: 'Տեղամաս', context: 'Համատեքստ', original: 'Կոորդինատները հաշվետվությունից', parsed: 'Վերլուծված է համակարգով', source: 'Աղբյուր', page: 'էջ', table: 'աղ.', review: 'Ստուգում', required: 'պահանջում է ստուգում', reviewCount: 'ստուգում են պահանջում', anomalyHidden: 'կոորդինատային անոմալիա չի ցուցադրվում հիմնական պլանում', hide: 'Թաքցնել', showAnomalies: 'Ցույց տալ անոմալ գրառումները', search: 'Որոնել հորատանցքի կոդով', allSites: 'Բոլոր տեղամասերը', xAxis: 'X (տեղային, միավոր)', yAxis: 'Y (տեղային, միավոր)', selectPoint: 'Ընտրեք կետ կամ տող՝ հորատանցքի մանրամասները տեսնելու համար։', anomalyTitle: 'Անոմալ կոորդինատներ (սկզբնական արժեքները պահպանված են և պլանում չեն ցուցադրվում)', code: 'Կոդ', reportCode: 'Հաշվետվությունում', xLocal: 'X (տեղ.)', yLocal: 'Y (տեղ.)', showAll: 'Ցույց տալ բոլորը' },
  en: { inReport: 'in report', site: 'Site', context: 'Context', original: 'Coordinates from report', parsed: 'Parsed by the system', source: 'Source', page: 'p.', table: 'table', review: 'Review', required: 'requires review', reviewCount: 'require review', anomalyHidden: 'coordinate anomalies are excluded from the main plan', hide: 'Hide', showAnomalies: 'Show anomalous records', search: 'Search by borehole code', allSites: 'All sites', xAxis: 'X (local units)', yAxis: 'Y (local units)', selectPoint: 'Select a point or row to inspect the borehole.', anomalyTitle: 'Anomalous coordinates (original values retained and excluded from the plan)', code: 'Code', reportCode: 'In report', xLocal: 'X (local)', yLocal: 'Y (local)', showAll: 'Show all' },
};

const PointDetail: React.FC<{ p: LocalPlanPoint; lang: GeoUiLang }> = ({ p, lang }) => {
  const copy = LOCAL_COPY[lang];
  return (
  <div style={{ fontSize: 13, lineHeight: 1.7 }}>
    <div style={{ fontWeight: 700 }}>{p.borehole_code}
      {p.borehole_code_raw && p.borehole_code_raw !== p.borehole_code &&
        <span style={{ color: 'var(--ink-tertiary)', fontWeight: 400 }}> ({copy.inReport}: {p.borehole_code_raw})</span>}</div>
    {p.location && <div>{copy.site}: {p.location}</div>}
    {p.exploration_context && <div>{copy.context}: {p.exploration_context}</div>}
    <div style={{ fontWeight: 600, marginTop: 4 }}>{copy.original}</div>
    <div>X/Y: <code>{p.original_x_text ?? p.x}, {p.original_y_text ?? p.y}</code></div>
    <div style={{ fontWeight: 600, marginTop: 4 }}>{copy.parsed}</div>
    <div>X/Y: <code>{p.x}, {p.y}</code>{p.z != null && <> · H {p.z}</>}</div>
    {(p.source_page || p.source_table) && <div>{copy.source}: {copy.page} {p.source_page || '—'} · {copy.table} {p.source_table || '—'}</div>}
    <div>{copy.review}: {p.verification_status_ru || '—'}{p.review_required && <span style={{ color: 'var(--amber)' }}> · {copy.required}</span>}</div>
  </div>
  );
};

const LocalCoordinatePlan: React.FC<{ plan: LocalSpatialPlan; lang?: GeoUiLang }> = ({ plan, lang = 'ru' }) => {
  const copy = LOCAL_COPY[lang];
  const [selected, setSelected] = useState<LocalPlanPoint | null>(null);
  const [search, setSearch] = useState('');
  const [locFilter, setLocFilter] = useState('');
  const [showAnomalies, setShowAnomalies] = useState(false);
  const [tableAll, setTableAll] = useState(false);

  const locColor = useMemo(() => colorForLocation(plan.locations), [plan.locations]);

  const filtered = useMemo(() => plan.points.filter((p) =>
    (!locFilter || p.location === locFilter) &&
    (!search || p.borehole_code.toLowerCase().includes(search.toLowerCase()) ||
      (p.borehole_code_raw || '').toLowerCase().includes(search.toLowerCase()))
  ), [plan.points, locFilter, search]);

  // §11: main geometry = NON-anomalous points only; anomalies never relocated.
  const geom = useMemo(() => filtered.filter((p) => !p.anomalous), [filtered]);
  const anomalies = useMemo(() => filtered.filter((p) => p.anomalous), [filtered]);

  const { toX, toY } = useMemo(() => {
    if (!geom.length) return { toX: () => W / 2, toY: () => H / 2 };
    const xs = geom.map((p) => p.x), ys = geom.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const spanX = maxX - minX || 1, spanY = maxY - minY || 1;
    const scale = Math.min((W - 2 * PAD) / spanX, (H - 2 * PAD) / spanY);
    const offX = (W - scale * spanX) / 2, offY = (H - scale * spanY) / 2;
    return {
      toX: (x: number) => offX + (x - minX) * scale,
      toY: (y: number) => H - (offY + (y - minY) * scale),  // no clamping — geom is already in-range
    };
  }, [geom]);

  const pagedTable = tableAll ? filtered : filtered.slice(0, TABLE_PAGE);

  return (
    <div data-testid="local-coordinate-plan">
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{plan.title}</div>
      <div style={{ fontSize: 12, color: 'var(--amber)', background: 'var(--amber-bg)',
        border: '1px solid var(--amber)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
        {plan.banner}
      </div>
      {(plan.source_crs_text || plan.height_system_text) && (
        <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginBottom: 8 }}>
          {plan.source_crs_text}{plan.height_system_text ? ` · ${plan.height_system_text}` : ''}
          {plan.review_required_count > 0 && <> · {copy.reviewCount}: {plan.review_required_count}</>}
        </div>
      )}

      {anomalies.length > 0 && (
        <div data-testid="plan-anomaly-badge" style={{ fontSize: 12, marginBottom: 8 }}>
          <span style={{ color: 'var(--amber)' }}>{anomalies.length} {copy.anomalyHidden}</span>{' '}
          <button data-testid="plan-toggle-anomalies" onClick={() => setShowAnomalies((s) => !s)}
            style={{ background: 'none', border: 'none', color: 'var(--navy)', cursor: 'pointer' }}>
            {showAnomalies ? copy.hide : copy.showAnomalies}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <input data-testid="plan-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={copy.search} style={{ padding: '6px 10px', borderRadius: 6,
            border: '1px solid var(--border)', fontSize: 13, flex: '1 1 200px' }} />
        <select data-testid="plan-location-filter" value={locFilter} onChange={(e) => setLocFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}>
          <option value="">{copy.allSites}</option>
          {plan.locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {/* legend by участок */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6, fontSize: 12 }}>
        {plan.locations.map((l) => (
          <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 5, background: locColor[l], display: 'inline-block' }} />{l}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ink-tertiary)' }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, border: '1.5px dashed var(--ink-tertiary)', display: 'inline-block' }} />{copy.required}
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <svg width={W} height={H} data-testid="plan-svg"
          style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-card)' }}>
          {/* axis labels (local, NOT geographic) */}
          <text x={W / 2} y={H - 8} fontSize="11" fill="var(--ink-tertiary)" textAnchor="middle">{copy.xAxis}</text>
          <text x={14} y={H / 2} fontSize="11" fill="var(--ink-tertiary)" textAnchor="middle"
            transform={`rotate(-90 14 ${H / 2})`}>{copy.yAxis}</text>
          {geom.map((p, i) => {
            const cx = toX(p.x), cy = toY(p.y);
            const isSel = selected?.borehole_code === p.borehole_code;
            const col = p.location ? locColor[p.location] : 'var(--ink-tertiary)';
            return (
              <g key={p.borehole_code + i} style={{ cursor: 'pointer' }} onClick={() => setSelected(p)}>
                <circle cx={cx} cy={cy} r={isSel ? 7 : 4.5} fill={p.review_required ? '#fff' : col}
                  stroke={col} strokeWidth={p.review_required ? 2 : 1}
                  strokeDasharray={p.review_required ? '2,1.5' : undefined} />
                {isSel && <text x={cx + 9} y={cy + 4} fontSize="11" fill="var(--ink-primary)">{p.borehole_code}</text>}
              </g>
            );
          })}
        </svg>

        <div style={{ flex: '1 1 240px', minWidth: 220 }}>
          {selected ? (
            <div data-testid="plan-point-detail" style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
              <PointDetail p={selected} lang={lang} />
            </div>
          ) : (
            <div style={{ padding: 12, color: 'var(--ink-tertiary)', fontSize: 13 }}>
              {copy.selectPoint}
            </div>
          )}
        </div>
      </div>

      {showAnomalies && anomalies.length > 0 && (
        <div data-testid="plan-anomaly-list" style={{ marginTop: 10, padding: 10, border: '1px solid var(--amber)',
          borderRadius: 8, fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: 'var(--amber)', marginBottom: 6 }}>
            {copy.anomalyTitle}
          </div>
          {anomalies.map((p, i) => (
            <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '4px 0', cursor: 'pointer' }}
              onClick={() => setSelected(p)}>
              {p.borehole_code}: <code>{p.original_x_text ?? p.x}, {p.original_y_text ?? p.y}</code> · {p.location || '—'}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, maxHeight: 300, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{[copy.code, copy.reportCode, copy.site, copy.xLocal, copy.yLocal, copy.review].map((c) => (
            <th key={c} style={{ textAlign: 'left', padding: '5px 8px', borderBottom: '2px solid var(--border)',
              fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-tertiary)' }}>{c}</th>
          ))}</tr></thead>
          <tbody>
            {pagedTable.map((p, i) => (
              <tr key={p.borehole_code + i} data-testid="plan-row" onClick={() => setSelected(p)}
                style={{ cursor: 'pointer',
                  background: selected?.borehole_code === p.borehole_code ? 'var(--bg-subtle)' : undefined }}>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)' }}>{p.borehole_code}{p.anomalous && ' ⚠'}</td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', color: 'var(--ink-tertiary)' }}>{p.borehole_code_raw || '—'}</td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)' }}>{p.location || '—'}</td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', fontVariantNumeric: 'tabular-nums' }}>{p.x}</td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', fontVariantNumeric: 'tabular-nums' }}>{p.y}</td>
                <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)',
                  color: p.review_required ? 'var(--amber)' : 'var(--ink-secondary)' }}>
                  {p.review_required ? copy.review : (p.verification_status_ru || '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!tableAll && filtered.length > TABLE_PAGE && (
          <button data-testid="plan-show-all" onClick={() => setTableAll(true)}
            style={{ marginTop: 8, fontSize: 13, color: 'var(--navy)', background: 'none', border: 'none', cursor: 'pointer' }}>
            {copy.showAll} ({filtered.length})
          </button>
        )}
      </div>
    </div>
  );
};

export default LocalCoordinatePlan;
